import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import MapSearchBar from '../../components/feature/MapSearchBar';
import MapFilters from '../../components/feature/MapFilters';
import MapSidebar from '../../components/feature/MapSidebar';
import InteractiveMap from '../../components/feature/InteractiveMap';
import PlaceDetailModal from '../../components/feature/PlaceDetailModal';
import BottomNav from '../../components/feature/BottomNav';
import { places } from '../../mocks/places';

export default function MapPage() {
  const { i18n } = useTranslation();
  const [selectedPlace, setSelectedPlace] = useState<typeof places[0] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPrices, setSelectedPrices] = useState<number[]>([]);
  const [openNow, setOpenNow] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState<number | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sortBy, setSortBy] = useState<'rating' | 'distance' | 'popularity' | 'price'>('rating');

  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = 'Interactive Map - Discover Ulaanbaatar | Find Places Near You';

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.replace('meta[', '').replace(']', '');
        const eqIdx = parts.indexOf('=');
        el.setAttribute(parts.slice(0, eqIdx), parts.slice(eqIdx + 1).replace(/"/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', 'Explore Ulaanbaatar with our interactive map. Find restaurants, bars, attractions, cafes, hotels, and more.');
    setMeta('meta[name="keywords"]', 'content', 'Ulaanbaatar map, Mongolia places map, find restaurants Ulaanbaatar, interactive map UB');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/map`);

    return () => {
      const s = document.querySelector('script[data-page-schema="map"]');
      if (s) s.remove();
    };
  }, []);

  const handleNearMeClick = () => {
    if (!navigator.geolocation) return;
    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setSelectedRadius(1);
        setIsLoadingLocation(false);
      },
      () => setIsLoadingLocation(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getFilteredPlaces = () => {
    let filtered = [...places];
    if (selectedCategory !== 'all') filtered = filtered.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());
    if (selectedPrices.length > 0) filtered = filtered.filter(p => selectedPrices.includes(p.budget));
    if (openNow) filtered = filtered.filter(p => p.isOpen);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.nameMn.toLowerCase().includes(q) ||
        p.nameEn.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }

    const withDistance = filtered.map(p => ({
      ...p,
      distance: userLocation ? calculateDistance(userLocation.lat, userLocation.lng, p.lat, p.lng) : null,
    }));

    let final = withDistance;
    if (userLocation && selectedRadius) {
      final = withDistance.filter(p => p.distance !== null && p.distance <= selectedRadius);
    }

    switch (sortBy) {
      case 'distance': if (userLocation) final.sort((a, b) => (a.distance || 0) - (b.distance || 0)); break;
      case 'rating': final.sort((a, b) => b.rating - a.rating); break;
      case 'popularity': final.sort((a, b) => b.views - a.views); break;
      case 'price': final.sort((a, b) => a.budget - b.budget); break;
    }
    return final;
  };

  const filteredPlaces = getFilteredPlaces();

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      <Header />

      {/* Fixed top bar: search + filters, sits right below header */}
      <div className="fixed left-0 right-0 z-30" style={{ top: '64px' }}>
        <MapFilters
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          selectedPrices={selectedPrices}
          onPriceChange={setSelectedPrices}
          openNow={openNow}
          onOpenNowChange={setOpenNow}
          selectedRadius={selectedRadius}
          onRadiusChange={setSelectedRadius}
          onNearMeClick={handleNearMeClick}
          isLoadingLocation={isLoadingLocation}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </div>

      {/* Main content area — offset by header + filters bar height */}
      <main
        id="map-main"
        className="flex flex-1 flex-col lg:flex-row overflow-hidden"
        style={{ marginTop: '64px', paddingTop: 'var(--filters-bar-height, 120px)' }}
      >
        <MapSidebar
          places={filteredPlaces}
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onPlaceSelect={setSelectedPlace}
          selectedPlace={selectedPlace}
          userLocation={userLocation}
        />

        {/* Map fills remaining space */}
        <div className="flex-1 relative" style={{ minHeight: '300px' }}>
          <InteractiveMap
            places={filteredPlaces}
            selectedPlace={selectedPlace}
            onPlaceSelect={setSelectedPlace}
            userLocation={userLocation}
            radiusKm={selectedRadius ?? undefined}
          />
        </div>
      </main>

      {selectedPlace && (
        <PlaceDetailModal
          place={selectedPlace}
          onClose={() => setSelectedPlace(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}
